import { useCallback, useMemo, useState } from 'react';
import {
  AssociatedMedicineValue,
  TreatmentMaterialValue,
  materialsFromServer,
  materialsToPayload,
  toAssociatedMedicineValues,
  withoutRowStock,
} from 'src/sections/@dashboard/profile/components/associatedMedicineStock';
import { useWarehouseStockEnabled } from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import {
  TreatmentFormSnapshot,
  TreatmentFormValues,
} from './treatmentFormValues';
import {
  GetProfileTreatmentDto,
  GetTreatmentDto,
  GetTreatmentOverviewItemDto,
  TreatmentMaterialInputDto,
} from 'woofwoof-api';

/** A treatment the form edits: a patient's or visit's entry, or an overview row. */
export type SavedTreatment =
  | GetProfileTreatmentDto
  | GetTreatmentOverviewItemDto;

export interface TreatmentLinesPayload {
  associatedMedicines: AssociatedMedicineValue[];
  /** Clinic context only; absent leaves a treatment's materials unchanged. */
  materials?: TreatmentMaterialInputDto[];
}

export interface TreatmentFormLines {
  medicines: AssociatedMedicineValue[];
  setMedicines: (next: AssociatedMedicineValue[]) => void;
  materials: TreatmentMaterialValue[];
  setMaterials: (next: TreatmentMaterialValue[]) => void;
  /**
   * "Materiały" only for a clinic role that edits stock, while the Warehouse
   * is enabled (revision R1), and only for a treatment whose materials are
   * known: an overview row carries none, so its edit leaves them untouched.
   */
  materialsEditable: boolean;
  /** The edited treatment's lines, ids kept (R2-3); none for a new one. */
  showSaved: (treatment?: SavedTreatment) => void;
  /** A catalogue template's lines, as new lines of a new record. */
  showTemplate: (template: GetTreatmentDto) => void;
  /** R3-4: what the form shows, with these lines, for the series to keep. */
  snapshot: (
    form: TreatmentFormValues,
    catalogTreatment: GetTreatmentDto | null,
  ) => TreatmentFormSnapshot;
  /** R3-4: the lines of an unconfirmed write's snapshot, as they were sent. */
  showFrozen: (
    lines: Pick<TreatmentFormSnapshot, 'medicines' | 'materials'>,
  ) => void;
  toPayload: () => TreatmentLinesPayload;
}

/** A saved line keeps its loaded stock; a new one takes none. */
function savedStockOnly(row: AssociatedMedicineValue): AssociatedMedicineValue {
  return row.id ? row : withoutRowStock(row);
}

export interface TreatmentFormLinesOptions {
  /**
   * A Facility's write (a clinic is selected): drug rows keep their stock.
   * Only an owner's write strips it (the owner API refuses stock fields); a
   * clinic write without a row's stock would reverse that row's Stock Issue.
   */
  facilityWrite: boolean;
  /**
   * The selected role edits stock (a professional): the rows' "Z magazynu"
   * and "Materiały". Another clinic role edits neither, so its write sends
   * the rows as loaded and leaves the materials alone.
   */
  stockEditable: boolean;
}

/** The drug lines and materials beside `ProfileTreatmentFormModal`'s fields. */
export function useTreatmentFormLines({
  facilityWrite,
  stockEditable,
}: TreatmentFormLinesOptions): TreatmentFormLines {
  const [rows, setMedicines] = useState<AssociatedMedicineValue[]>([]);
  const [materials, setMaterials] = useState<TreatmentMaterialValue[]>([]);
  // An overview row has no materials; sending [] would reverse them all.
  const [materialsKnown, setMaterialsKnown] = useState(true);
  const warehouseEnabled = useWarehouseStockEnabled();
  const materialsEditable =
    facilityWrite && stockEditable && warehouseEnabled && materialsKnown;
  // While the Warehouse is disabled a new line (a template's included) takes
  // nothing, like the template's materials: the backend would refuse it with
  // 422 WAREHOUSE_DISABLED, F-8 would hold that create, and with it every
  // create on the patient and visit. A saved line keeps its loaded stock, so
  // it moves nothing (R1). The rows on screen are the rows sent.
  const medicines = useMemo(
    () =>
      facilityWrite && !warehouseEnabled ? rows.map(savedStockOnly) : rows,
    [rows, facilityWrite, warehouseEnabled],
  );

  const showSaved = useCallback((treatment?: SavedTreatment): void => {
    setMedicines(
      toAssociatedMedicineValues(treatment?.associatedMedicines ?? []),
    );
    const saved =
      treatment && 'materials' in treatment ? treatment.materials : undefined;
    setMaterials(materialsFromServer(saved));
    setMaterialsKnown(!treatment || Array.isArray(saved));
  }, []);

  const showTemplate = useCallback((template: GetTreatmentDto): void => {
    // The template's line ids are not the new record's.
    setMedicines(
      toAssociatedMedicineValues(template.associatedMedicines ?? [], false),
    );
    setMaterials(materialsFromServer(template.defaultMaterials, false));
    setMaterialsKnown(true);
  }, []);

  const showFrozen = useCallback(
    (frozen: Pick<TreatmentFormSnapshot, 'medicines' | 'materials'>): void => {
      setMedicines(frozen.medicines);
      setMaterials(frozen.materials);
    },
    [],
  );

  return {
    medicines,
    setMedicines,
    materials,
    setMaterials,
    materialsEditable,
    showSaved,
    showTemplate,
    showFrozen,
    snapshot: (form, catalogTreatment) => ({
      form,
      medicines,
      materials,
      catalogTreatment,
    }),
    // While the Warehouse is disabled saved drug rows keep their loaded
    // stock, so an unchanged row moves nothing; materials are left alone (R1).
    toPayload: (): TreatmentLinesPayload => ({
      associatedMedicines: facilityWrite
        ? medicines
        : medicines.map(withoutRowStock),
      ...(materialsEditable
        ? { materials: materialsToPayload(materials) }
        : {}),
    }),
  };
}
